import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { FilterLabelStore } from '../services/filter-label-store.service';
import { SalesStatisticsQueryService } from '../services/sales-statistics-query.service';
import { SalesStatisticsTemplate } from '../types/sales-statistics-template';
import {
  StatisticsFilterArgs,
  templateToStatisticsArgs,
} from './agent-filter-mapping';

/** Filter keys whose values carry a resolvable label in the label store. */
const LABELLED_FIELDS = [
  'customer',
  'product',
  'productGroup',
  'mainGroup',
  'subGroup',
  'subSubGroup',
] as const;

export interface SalesStatisticsSnapshot {
  args: StatisticsFilterArgs;
  /** `<field>.<value>` → readable label, so the agent can name what is filtered. */
  labels: Record<string, string>;
}

/**
 * Root-level handle on the route-scoped `SalesStatisticsQueryService`.
 *
 * The chat lives outside the statistics route (page and shell panel), so it
 * cannot inject that service; the page hands it over while it is open.
 */
@Injectable({ providedIn: 'root' })
export class SalesStatisticsAgentBridge {
  private readonly labelStore = inject(FilterLabelStore);

  readonly active = signal<SalesStatisticsQueryService | null>(null);

  attach(service: SalesStatisticsQueryService, destroyRef: DestroyRef): void {
    this.active.set(service);
    destroyRef.onDestroy(() => {
      if (this.active() === service) this.active.set(null);
    });
  }

  /** The visible filters, in ai-mcp's vocabulary; `null` when the page is closed. */
  snapshot(): SalesStatisticsSnapshot | null {
    const service = this.active();
    if (!service) return null;
    const template = service.getTemplateValue();
    return { args: templateToStatisticsArgs(template), labels: this.labelsFor(template) };
  }

  /**
   * Merge-patches the live filters. `applyTemplate` nulls every array key it
   * does not receive, so the current value has to be spread underneath.
   */
  applyPatch(patch: Partial<SalesStatisticsTemplate>): boolean {
    const service = this.active();
    if (!service) return false;
    service.applyTemplate({ ...service.getTemplateValue(), ...patch });
    service.notifyChatbotApply();
    return true;
  }

  private labelsFor(template: SalesStatisticsTemplate): Record<string, string> {
    const labels: Record<string, string> = {};
    for (const field of LABELLED_FIELDS) {
      for (const value of template[field] ?? []) {
        const label = this.labelStore.resolve(field, value);
        if (label) labels[`${field}.${value}`] = label;
      }
    }
    return labels;
  }
}
